/**
 * TanStack Query infrastructure for patient timeline data fetching.
 */
import { useInfiniteQuery } from "@tanstack/react-query";

import type {
  PaginatedTimelineResponse,
  TimelineFilters,
} from "@/types/patient-timeline";

import { requestData } from "@/api/api-config";

/**
 * Fetches paginated timeline activities with filters.
 */
async function fetchPaginatedActivities(
  patientId: string,
  filters: TimelineFilters,
  timezone: string,
  page: number,
  perPage: number,
): Promise<PaginatedTimelineResponse> {
  const searchParams: Record<string, string | string[]> = {
    page: String(page),
    per_page: String(perPage),
    timezone,
  };
  if (filters.startDate) searchParams.start_date = filters.startDate;
  if (filters.endDate) searchParams.end_date = filters.endDate;
  if (filters.search) searchParams.search = filters.search;
  const ALL_TYPES_COUNT = 8;
  if (
    filters.activityTypes.length > 0 &&
    filters.activityTypes.length < ALL_TYPES_COUNT
  ) {
    searchParams.activity_type = filters.activityTypes;
  }

  const result = await requestData<PaginatedTimelineResponse>(
    `user/patient/${patientId}/timeline/activities`,
    "get",
    { searchParams },
  );
  return result ?? { page, perPage, total: 0, activities: [] };
}

export const patientTimelineQueryKeys = {
  all: ["user", "patient", "timeline"] as const,
  paginated: (patientId: string, filters: TimelineFilters, timezone: string) =>
    [
      ...patientTimelineQueryKeys.all,
      "paginated",
      patientId,
      filters,
      timezone,
    ] as const,
};

export const patientTimelineQueryOptions = {
  paginatedActivities: (
    patientId: string,
    filters: TimelineFilters,
    timezone: string,
    perPage = 25,
  ) => ({
    queryKey: patientTimelineQueryKeys.paginated(patientId, filters, timezone),
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchPaginatedActivities(
        patientId,
        filters,
        timezone,
        pageParam,
        perPage,
      ),
    getNextPageParam: (lastPage: PaginatedTimelineResponse) =>
      lastPage.page * lastPage.perPage < lastPage.total
        ? lastPage.page + 1
        : undefined,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  }),
};

/**
 * Infinite query for paginated timeline activities.
 *
 * Flattens pages into a single activities array via select.
 */
export function useInfiniteTimelineActivities(
  patientId: string,
  filters: TimelineFilters,
  timezone: string,
  perPage = 25,
) {
  return useInfiniteQuery({
    ...patientTimelineQueryOptions.paginatedActivities(
      patientId,
      filters,
      timezone,
      perPage,
    ),
    enabled: !!patientId,
    select: (data) => ({
      activities: data.pages.flatMap((page) => page.activities),
      total: data.pages[0]?.total ?? 0,
    }),
  });
}
